import { useCallback } from 'react'
import { generatePath, useLocation, useSearchParams } from 'react-router-dom'

import { PathParams, useRouteParams } from '../useRouteParams'
import { SearchParam, updateSearchParams } from '../useSearchParams'

type ParamProps = {
  pathParams?: PathParams
  queryParams?: SearchParam
  clearQueryParams?: boolean
}

const allowedQueryParamKeys = [
  'check_in',
  'check_out',
  'rooms',
  'adults',
  'children',
  'infants',
  'start_date',
  'page',
  'order',
  'property_order',
  'room_order',
  'plan_order',
  'show_all',
  'facility',
  'transaction',
  'area',
  'max_price',
  'min_price',
  'equipment',
  'room', // For secret room
  'discount_code', // Coupon code carried from campaign URLs
  'invoice_key', // Invoice billing key carried from the plan's invoice URL
  'from_plan_rooms', // Marks plan-first origin so registrations Back returns there
]

export const useCreateNextPath = () => {
  const routeParams = useRouteParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const onCreateNextPath = useCallback(
    (path: string, params?: ParamProps) => {
      const search =
        params && params.queryParams
          ? `?${updateSearchParams(
              searchParams,
              params.queryParams
            ).toString()}`
          : location.search

      const urlSearchParams = new URLSearchParams(
        Array.from(new URLSearchParams(search)).filter(([key]) =>
          allowedQueryParamKeys.includes(key)
        )
      )
      const nextPathParams = { ...routeParams, ...params?.pathParams }
      // invoice_key opens only the plan of the invoice URL, so it is dropped once
      // the guest moves to another property or plan; carried along it would turn an
      // unrelated booking into an "invalid invoice URL" error (SB-3056).
      if (
        nextPathParams.property !== routeParams.property ||
        (routeParams.plan !== undefined &&
          nextPathParams.plan !== routeParams.plan)
      ) {
        urlSearchParams.delete('invoice_key')
      }
      if (params && params.clearQueryParams) {
        return {
          pathname: generatePath(path, {
            ...routeParams,
            ...params?.pathParams,
          }),
          search: '',
        }
      }

      return {
        pathname: generatePath(path, nextPathParams),
        search: urlSearchParams.toString(),
      }
    },
    [routeParams, location, searchParams]
  )

  return { params: routeParams, onCreateNextPath }
}
